import React, { useMemo } from 'react';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import Header from '../components/header';
import Layout from '../components/ui/layout';
import Footer from '../components/footer';
import Button from '../components/ui/button';
import styled from 'styled-components';
import Product from '../components/product';
import Input from '../components/ui/input';
import '@lowes/product-viewer';
import Filters from '../components/filters';
import SEO from '../components/seo';
import screens from '../@style/screens';
import { categoriesCapitalized } from '../data/categories';
import Modal from '../components/ui/modal';
import DownloadForm from '../components/downloadForm';
import useCookie from '../hooks/useCookie';
import urlFilename from '../utils/urlFilename';

const GetAllProductsForRelated = graphql`
  query GetAllProductsForRelated {
    allProducts {
      nodes {
        _source {
          product {
            ...ProductsFields
          }
        }
      }
    }
  }
`;

interface Props {
  pageContext: { data: Queries.ProductsFieldsFragment };
}

const ProductWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1rem;
  @media ${screens.laptop} {
    grid-template-columns: 2fr 2fr;
    grid-gap: 2rem;
  }
`;

const DescriptionColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Product3D = styled.div`
  width: calc(100vw - 3rem);
  height: calc(100vw - 3rem);
  border-radius: 30px;
  @media ${screens.laptop} {
    width: 40vw;
    height: 40vw;
  }
`;

const StyledHR = styled.hr`
  border-top: 1px solid #00a3e0;
  width: 100%;
  margin: 50px 0 40px 0;
  @media ${screens.laptop} {
    margin: 70px 0 50px 0;
    border-width: 2px;
  }
`;

const StyledInputHR = styled.hr`
  border-top: 1px solid #00a3e0;
  width: 100%;
  margin: 40px 0 50px 0;
  @media ${screens.laptop} {
    border-width: 2px;
  }
`;

const RelatedProductsTitle = styled.h4`
  font-size: 17px;
  @media ${screens.laptop} {
    font-size: 26px;
  }
`;

const RelatedProductsGrid = styled.div`
  display: grid;
  margin-top: 30px;
  margin-bottom: 30px;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1.5rem;
  @media ${screens.laptop} {
    grid-template-columns: repeat(5, 1fr);
    grid-gap: 2.5rem;
  }
`;

const DivisionCopy = styled.h4`
  font-size: 15px;
  font-weight: 400;
  @media ${screens.laptopL} {
    font-size: 16px;
  }
  @media ${screens.standard} {
    font-size: 30px;
  }
`;

const BrandCopy = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 8px 0;
  @media ${screens.laptopL} {
    font-size: 40px;
    margin: 10px 0;
  }
  @media ${screens.standard} {
    font-size: 60px;
    margin: 18px 0;
  }
`;

const DescriptionCopy = styled.h3`
  font-size: 17px;
  font-weight: 400;
  margin-bottom: 15px;
  max-width: 400px;
  @media ${screens.laptopL} {
    max-width: 600px;
    font-size: 16px;
    margin-bottom: 30px;
  }
  @media ${screens.standard} {
    font-size: 30px;
    margin-bottom: 40px;
  }
`;

const LowesLinkWrapper = styled.div`
  display: flex;
  font-size: 17px;
  margin-top: 1rem;
  @media ${screens.laptop} {
    margin-top: 1.5rem;
  }
  @media ${screens.laptopL} {
    margin-top: 2rem;
    font-size: 16px;
  }
  @media ${screens.standard} {
    font-size: 30px;
  }
`;

const LowesLink = styled.a`
  text-decoration: underline;
`;

const ProductTemplate: React.FC<Props> = ({ pageContext: { data } }) => {
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [desiredProductToDownload, setDesiredProductToDownload] = React.useState('');
  const [cookie, updateCookie] = useCookie('downloadForm', 'not-filled');
  const { allProducts } =
    useStaticQuery<Queries.GetAllProductsForRelatedQuery>(GetAllProductsForRelated);

  const filteredProducts = useMemo(() => {
    return allProducts.nodes
      .filter(
        (product) =>
          product?._source?.product?.merchandisingHierarchy?.division ===
          data.merchandisingHierarchy?.division
      )
      .filter((product) => product._source?.product)
      .map((product) => ({ product: product._source?.product, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ product }) => product)
      .slice(0, 10); // shuffle products
  }, [allProducts.nodes, data.merchandisingHierarchy?.division]);

  const nonNullCategories = useMemo(
    () =>
      allProducts.nodes
        .filter((product) => product._source?.product?.merchandisingHierarchy?.divisionDescription)
        .map((productRaw) => {
          const division = productRaw._source?.product?.merchandisingHierarchy;
          if (division) {
            return `${division.divisionDescription}|${division.division}`;
          }
          return null;
        }),
    [allProducts.nodes]
  );

  const categoriesRaw = useMemo(() => [...new Set(nonNullCategories)], [nonNullCategories]);

  const categories = categoriesRaw.map((category) => {
    if (category) {
      const [name, id] = category.split('|');
      return {
        name: categoriesCapitalized[id] ?? name[0].toUpperCase() + name.substring(1).toLowerCase(),
        id,
      };
    }
    return {};
  });

  const downloadFormDate = new Date(parseInt(cookie, 10));
  const isDownloadFormDateValid =
    downloadFormDate instanceof Date && !isNaN(downloadFormDate as any);
  const downloadFormDateDifference = Math.floor(
    (new Date().getTime() - downloadFormDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isDownloadFormDateAcceptable = isDownloadFormDateValid && downloadFormDateDifference < 180;

  return (
    <>
      <SEO
        title={data.brand}
        description={data.description}
        image={urlFilename(data.epc?.alternative_3dThumbnailUrl)}
      />
      <Header />
      <Layout>
        <Input
          id="search-input"
          placeholder="Search our 3D product catalog"
          onEnter={(phrase?: string) => navigate('/search', { state: { phrase: phrase } })}
        />
      </Layout>
      <div style={{ maxWidth: '100vw' }}>
        <Filters
          items={categories.map((category) => ({
            name: category.name ?? '',
            onClick: () => navigate('/search', { state: { category: category.id } }),
          }))}
        />
      </div>
      <StyledInputHR />
      <Layout>
        <ProductWrapper>
          <Product3D>
            <product-viewer
              style={{ width: '100%', height: '100%', borderRadius: '30px' }}
              model-url={urlFilename(data.epc?.alternative_3dAssetUrl)}
              background-color="#ffffff"
            />
          </Product3D>
          <DescriptionColumn>
            <DivisionCopy>
              {categoriesCapitalized[data.merchandisingHierarchy?.division ?? ''] ?? ''}
            </DivisionCopy>
            <BrandCopy>{data?.brand}</BrandCopy>
            <DescriptionCopy>{data?.description}</DescriptionCopy>
            <Button
              link={
                isDownloadFormDateAcceptable
                  ? urlFilename(data.epc?.alternative_3dAssetUrl_UsdzModel)
                  : undefined
              }
              onClick={() => {
                if (!isDownloadFormDateAcceptable) {
                  setDesiredProductToDownload(
                    urlFilename(data.epc?.alternative_3dAssetUrl_UsdzModel)
                  );
                  setShowDownloadModal(true);
                }
              }}
            >
              Download usdz
            </Button>
            <Button
              link={
                isDownloadFormDateAcceptable
                  ? urlFilename(data.epc?.alternative_3dAssetUrl)
                  : undefined
              }
              onClick={() => {
                if (!isDownloadFormDateAcceptable) {
                  setDesiredProductToDownload(urlFilename(data.epc?.alternative_3dAssetUrl));
                  setShowDownloadModal(true);
                }
              }}
            >
              Download glb
            </Button>
            <LowesLinkWrapper>
              View on&nbsp;
              {data.pdURL && data.omniItemId && (
                <LowesLink id={data.omniItemId} href={'https://lowes.com'.concat(data.pdURL)} target="_blank" >
                  Lowes.com
                </LowesLink>
              )}
            </LowesLinkWrapper>
          </DescriptionColumn>
        </ProductWrapper>
      </Layout>
      <StyledHR />
      <Layout>
        {filteredProducts && (
          <>
            <RelatedProductsTitle>You may also like</RelatedProductsTitle>
            <RelatedProductsGrid>
              {filteredProducts.map(
                (product, relatedIndex) =>
                  product && <Product key={`product-related-${relatedIndex}`} data={product} />
              )}
            </RelatedProductsGrid>
          </>
        )}
      </Layout>
      <Footer />
      <Modal onClose={() => setShowDownloadModal(false)} show={showDownloadModal}>
        <DownloadForm
          onDone={() => {
            setShowDownloadModal(false);
            updateCookie(new Date().getTime());
            if (desiredProductToDownload) {
              window.open(desiredProductToDownload);
            }
          }}
        />
      </Modal>
    </>
  );
};

export default ProductTemplate;

export { RelatedProductsGrid };
